/**
 * Mirrors useCardAvailability: owned minus committed deck usage.
 *
 * Deck usage falls into three tiers:
 * - committed: active decks hold the physical copies, so they consume availability
 * - planned:   brewing decks are theoretical builds, so they never reserve a copy
 * - maybeboard: never reserves, regardless of deck status
 * Archived decks are excluded entirely.
 */
import { atom } from 'jotai';

import { collectionStateAtom } from '@/features/collection/state/collectionAtom';
import { allDeckCardsAtom } from '@/features/decks/state/allDeckCards';
import { decksStateAtom } from '@/features/decks/state/decksAtom';
import { getOracleId } from '@/lib/cardUtils';
import { cardIndexByIdAtom, type MinimalCard } from '@/services/cardDatabase';
import type { DeckStatus, DeckZone } from '@/types/deck';

import { fullCardCacheAtom } from './cardCache';

export type DeckUsageKind = 'committed' | 'planned' | 'maybeboard';

/** Returns null when the usage should be ignored (archived or unknown deck). */
export function classifyDeckUsage(deckStatus: DeckStatus | undefined, zone: DeckZone): DeckUsageKind | null {
  if (!deckStatus || deckStatus === 'archived') return null;
  if (zone === 'maybeboard') return 'maybeboard';
  return deckStatus === 'active' ? 'committed' : 'planned';
}

export const availabilityCardsByScryfallIdAtom = atom((get) => {
  const collection = get(collectionStateAtom);
  const deckCards = get(allDeckCardsAtom);
  const fullCards = get(fullCardCacheAtom);
  const index = get(cardIndexByIdAtom);
  const printingIds = new Set([
    ...(collection.data ?? []).map((card) => card.scryfall_id),
    ...(deckCards.data ?? []).map((card) => card.scryfall_id),
  ]);
  const cards = new Map<string, Pick<MinimalCard, 'id' | 'oracle_id' | 'name_lower' | 'set' | 'collector_number'>>();

  for (const printingId of printingIds) {
    const fullCard = fullCards.get(printingId);
    const card = fullCard ?? index.get(printingId);
    if (card) {
      cards.set(printingId, {
        id: printingId,
        oracle_id: fullCard ? getOracleId(fullCard) : card.oracle_id,
        name_lower: card.name_lower,
        set: card.set,
        collector_number: card.collector_number,
      });
    }
  }

  return cards;
});

/** Owned copies aggregated by oracle id, resolving reversible faces and falling back to the index. */
export const collectionOwnedByOracleIdAtom = atom<Map<string, number>>((get) => {
  const collection = get(collectionStateAtom);
  const cardsById = get(availabilityCardsByScryfallIdAtom);
  const owned = new Map<string, number>();

  for (const card of collection.data ?? []) {
    const oracleId = cardsById.get(card.scryfall_id)?.oracle_id;
    if (!oracleId) continue;
    owned.set(oracleId, (owned.get(oracleId) ?? 0) + card.quantity);
  }

  return owned;
});

export const collectionAvailableByOracleIdAtom = atom<Map<string, number>>((get) => {
  const decks = get(decksStateAtom);
  const deckCards = get(allDeckCardsAtom);
  const cardsById = get(availabilityCardsByScryfallIdAtom);
  const available = new Map(get(collectionOwnedByOracleIdAtom));

  const statusByDeckId = new Map((decks.data ?? []).map((deck) => [deck.id, deck.status]));

  for (const card of deckCards.data ?? []) {
    if (classifyDeckUsage(statusByDeckId.get(card.deck_id), card.zone) !== 'committed') continue;
    const oracleId = cardsById.get(card.scryfall_id)?.oracle_id;
    if (!oracleId) continue;
    available.set(oracleId, (available.get(oracleId) ?? 0) - card.quantity);
  }

  return available;
});

/** Owned copies aggregated by card name, since printings are interchangeable when deck building. */
export const collectionOwnedByNameAtom = atom<Map<string, number>>((get) => {
  const collection = get(collectionStateAtom);
  const cardsById = get(availabilityCardsByScryfallIdAtom);
  const owned = new Map<string, number>();

  for (const card of collection.data ?? []) {
    const nameLower = cardsById.get(card.scryfall_id)?.name_lower;
    if (!nameLower) continue;
    owned.set(nameLower, (owned.get(nameLower) ?? 0) + card.quantity);
  }

  return owned;
});

export interface DeckUsageByName {
  /** deck id -> quantity held by active decks */
  committed: Map<string, number>;
  /** deck id -> quantity planned by brewing decks */
  planned: Map<string, number>;
}

/**
 * Deck usage aggregated by card name, keyed per deck so a caller can exclude its own deck.
 * Computed once here rather than per row, since deck lists render hundreds of rows.
 */
export const deckUsageByNameAtom = atom<Map<string, DeckUsageByName>>((get) => {
  const decks = get(decksStateAtom);
  const deckCards = get(allDeckCardsAtom);
  const cardsById = get(availabilityCardsByScryfallIdAtom);
  const statusByDeckId = new Map((decks.data ?? []).map((deck) => [deck.id, deck.status]));
  const usage = new Map<string, DeckUsageByName>();

  for (const card of deckCards.data ?? []) {
    const kind = classifyDeckUsage(statusByDeckId.get(card.deck_id), card.zone);
    if (kind !== 'committed' && kind !== 'planned') continue;
    const nameLower = cardsById.get(card.scryfall_id)?.name_lower;
    if (!nameLower) continue;

    let entry = usage.get(nameLower);
    if (!entry) {
      entry = { committed: new Map(), planned: new Map() };
      usage.set(nameLower, entry);
    }
    const byDeck = entry[kind];
    byDeck.set(card.deck_id, (byDeck.get(card.deck_id) ?? 0) + card.quantity);
  }

  return usage;
});
